import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import { languageAtom, receptionCompanyIdAtom, volumeAtom } from "~/Atom";
import ReceptionHeader from "~/components/ReceptionHeader";
import { getPhoneBooksEntity } from "~/utils/receptionDB";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import type { GroupEntity, UserEntity } from "~/utils/receptionDB.types";
import { PlaySound } from "~/SoundUtils";
import { runReceptionBack } from "~/utils/receptionNavigation";
import "~/css/reception/VisitorSelectionPage.css";
import "~/css/reception/ReceptionButton.css";
import { ROUTE } from "~/constants/routes";
import {
  type SearchResult,
  type VisitorSelectionNavigationState,
  searchEntities,
  buildGroupConfirmState,
  buildUserConfirmState,
} from "~/utils/visitorSelectionUtils";

export type { VisitorSelectionNavigationState };

/**
 * 来訪先選択ページ。
 * グループ一覧またはURLパラメータによる検索結果をボタン形式で表示する。
 *
 * 主な責務:
 * - navigate.state の groupName・userName に基づいて検索結果を表示する
 * - パラメータなしの場合は isRecept=true のトップレベルグループ一覧を表示する
 * - 選択・戻るボタン押下時に効果音を再生する
 */
// eslint-disable-next-line complexity
const VisitorSelectionPage: React.FC = () => {
  const [language] = useAtom(languageAtom);
  const [volume] = useAtom(volumeAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const [groups, setGroups] = useState<GroupEntity[]>([]);
  const [searchResult, setSearchResult] = useState<SearchResult>({ kind: "none" });
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const titleId = React.useId();

  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });
  // ユーザー選択時にグループ名を逆引きするための全グループリストキャッシュ
  const allGroupsRef = useRef<GroupEntity[]>([]);

  const pageState = (location.state as VisitorSelectionNavigationState | null) ?? null;
  const groupName = pageState?.department ?? null;
  const userName = pageState?.personInCharge ?? null;
  const userNameKana = pageState?.personInChargeKana ?? null;
  const visitorName = pageState?.visitorName ?? null;
  const companyName = pageState?.companyName ?? null;
  // companyId は displayId であるため、groupList から対応する groupId を解決する
  const companyDisplayId = receptionCompanyId === 0 ? null : receptionCompanyId;
  const hasSearchParams =
    (groupName !== null && groupName.trim() !== "") ||
    (userName !== null && userName.trim() !== "");

  // IndexedDB からグループ一覧または検索結果を取得する
  useEffect(() => {
    const loadData = async () => {
      try {
        const entity = await getPhoneBooksEntity();
        if (!entity) return;

        // 全グループリストを参照用に保存する
        allGroupsRef.current = entity.groupList;

        // companyDisplayId から実際の groupId を解決する
        const rootGroupId =
          companyDisplayId !== null
            ? (entity.groupList.find((g) => g.displayId === companyDisplayId)?.groupId ?? null)
            : null;

        if (hasSearchParams) {
          // state に検索条件がある場合は検索ロジックを適用する
          const result = searchEntities(entity, groupName, userName, userNameKana, rootGroupId);
          setSearchResult(result);
        } else {
          // パラメータなしの場合: companyIdが有効ならその直下の子グループ、なければトップレベルを表示する
          const topLevel = entity.groupList
            .filter((g) =>
              rootGroupId !== null
                ? g.parentGroupId === rootGroupId && g.isRecept
                : g.parentGroupId === null && g.isRecept
            )
            .sort((a, b) => a.order - b.order);
          setGroups(topLevel);
        }
      } catch (error) {
        console.error("来訪先データの読み込みに失敗しました:", error);
      }
    };
    void loadData();
  }, [groupName, userName, userNameKana, hasSearchParams, companyDisplayId]);

  // 現在の言語コードに対応するグループ名を返す
  const getGroupName = useCallback(
    (group: GroupEntity): string => {
      const langCode = language.substring(0, 2);
      const nameItem = group.names.find((n) => n.languageCode === langCode);
      return nameItem?.name ?? group.names[0]?.name ?? "";
    },
    [language]
  );

  // 現在の言語コードに対応するユーザー名を返す
  const getUserName = useCallback(
    (user: UserEntity): string => {
      const langCode = language.substring(0, 2);
      const nameItem = user.names.find((n) => n.languageCode === langCode);
      return nameItem?.name ?? user.names[0]?.name ?? "";
    },
    [language]
  );

  const handleGroupSelect = useCallback(
    (group: GroupEntity) => {
      PlaySound("push", volume);
      navigate(ROUTE.RECEPTION.CONVERSATION.VISITOR_CONFIRMATION, {
        state: buildGroupConfirmState(group.groupId, groupName, visitorName, companyName),
      });
    },
    [volume, navigate, groupName, visitorName, companyName]
  );

  const handleUserSelect = useCallback(
    (user: UserEntity) => {
      PlaySound("push", volume);
      const parentGroup = allGroupsRef.current.find((g) => g.groupId === user.groupId);
      navigate(ROUTE.RECEPTION.CONVERSATION.VISITOR_CONFIRMATION, {
        state: buildUserConfirmState(
          user,
          parentGroup?.groupId,
          groupName,
          userName,
          visitorName,
          companyName
        ),
      });
    },
    [volume, navigate, groupName, userName, visitorName, companyName]
  );

  const handleBack = useCallback(() => {
    runReceptionBack({
      kind: "router-history",
      navigate,
    });
  }, [navigate, companyName, visitorName]);

  useInactivityTimeout(navigate);

  // 表示するアイテム一覧を算出する（検索結果またはデフォルトグループ）
  const displayItems = useMemo(() => {
    if (hasSearchParams) {
      return searchResult;
    }
    return { kind: "groups" as const, items: groups };
  }, [hasSearchParams, searchResult, groups]);

  const titleText = useMemo(() => t("visitorSelection.title").trim(), [t]);

  // タイトルテキストをTTSで読み上げる（表示件数が2件以上の場合のみ）
  useEffect(() => {
    if (displayItems.kind === "none" || displayItems.items.length <= 1) return;
    void playWithCooldown(titleText).catch((error) => {
      console.error("visitorSelection playVoice failed:", error);
    });
  }, [playWithCooldown, displayItems, titleText]);

  return (
    <div className="App-page visitor-selection-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />

      <main className="visitor-selection-page__content">
        <section className="visitor-selection-page__shell" aria-labelledby={titleId}>
          <div className="visitor-selection-page__title-group">
            <h1 id={titleId} className="visitor-selection-page__title typo-h1">
              {titleText}
            </h1>
          </div>

          <div className="visitor-selection-page__results">
            <div className="visitor-selection-page__scroll">
              <div className="visitor-selection-page__grid">
                {displayItems.kind === "groups" &&
                  displayItems.items.map((group) => {
                    const groupLabel = getGroupName(group);
                    return (
                      <button
                        type="button"
                        key={group.groupId}
                        className="visitor-selection-page__button reception-button--large"
                        onClick={() => handleGroupSelect(group)}
                        aria-label={groupLabel}
                      >
                        <span className="visitor-selection-page__button-label typo-h3">
                          {groupLabel}
                        </span>
                      </button>
                    );
                  })}

                {displayItems.kind === "users" &&
                  displayItems.items.map((user) => {
                    const userLabel = getUserName(user);
                    return (
                      <button
                        type="button"
                        key={user.groupUserId}
                        className="visitor-selection-page__button reception-button--large"
                        onClick={() => handleUserSelect(user)}
                        aria-label={userLabel}
                      >
                        <span className="visitor-selection-page__button-label typo-h3">
                          {userLabel}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VisitorSelectionPage;
